import { uuid, type MatchConfig } from '@nt2/engine';
import { convertOldRules } from './rules.js';
import { DEFAULT_COUNTDOWN, type CountdownStyle } from '../audio/countdown.js';
import type { ThemeId } from '../audio/engine.js';
import { DEFAULT_SCENE_GAME, DEFAULT_SCENE_MENU, sceneFits, type GridStrength, type SceneId } from '../ui/scenes.js';

/**
 * Per-device preferences. These are conveniences, not game state — every read
 * is defensive because storage throws in private windows on some browsers and
 * a thrown preference read must never take the game down with it.
 */
export interface Prefs {
  name: string;
  theme: 'dark' | 'light' | 'system';
  contrast: 'normal' | 'high';
  /**
   * Background scenes, chosen separately for the menus and the game screen.
   * A menu is looked at for ten seconds; a board is stared at for twenty
   * minutes with a hundred letters on it, and the two want different things.
   */
  sceneMenu: SceneId;
  sceneGame: SceneId;
  /** How strongly the board's squares are drawn. */
  grid: GridStrength;
  muted: boolean;
  soundTheme: ThemeId;
  volume: number;
  hintsEnabled: boolean;
  /** Ids of individual hints the player has dismissed for good. */
  hintsSeen: string[];
  tileSize: number;
  showRunTags: boolean;
  lastRoom: string;
  /** Read the demonstration's commentary out loud. Off until asked for. */
  narrate: boolean;
  /**
   * Which way a tapped or typed letter goes: the next free square across, or
   * the next free square down. Also flipped by tapping the cursor square, or
   * Space.
   */
  typeDir: 'across' | 'down';
  /** What the countdown before a table's match sounds like on this device. */
  countdownSound: CountdownStyle;
  /** The speech voice for the spoken countdowns ('' = the device's usual English voice). */
  countdownVoice: string;
  /**
   * Let a tap choose the direction: under a letter means down, after one
   * means across, and a tap on a letter builds through it the free way.
   */
  autoDir: boolean;
  /**
   * How far a letter dragged from the rack has to travel, as a fraction of
   * the way to where it lands. 1 is off: the letter lands under the finger.
   * At 0.5 moving it half-way is enough, and a green square shows where it
   * will go.
   */
  dragReach: number;
  /**
   * How close to the middle of a letter a drop has to be to swap with it,
   * rather than landing on the free square beside it. 0.75 means the drop has
   * to come 75% of the way in from the letter's edge. 1 never swaps.
   */
  swapGuard: number;
  /**
   * The rules this device last played with, so a host does not have to set
   * them up again every time. Stored as a patch rather than a whole config:
   * a saved config from an older build could carry a setting that no longer
   * exists, or miss one that now does, and spreading a patch over the current
   * defaults is immune to both.
   */
  lastRules: Partial<MatchConfig>;
}

const KEY = 'nt2.prefs.v1';
/** Raised on `window` whenever a preference is set. */
export const PREFS_CHANGED = 'nt2:prefs';

const DEFAULTS: Prefs = {
  name: '',
  /**
   * Dark by default rather than following the system.
   *
   * A board of ivory tiles on a dark field is what this game was designed
   * around, and light mode is the second theme rather than the first. Handing
   * the choice to the operating system meant most phones opened on the one it
   * looks worse in. Anybody who prefers light says so once, and it is
   * remembered.
   */
  theme: 'dark',
  contrast: 'normal',
  sceneMenu: DEFAULT_SCENE_MENU,
  sceneGame: DEFAULT_SCENE_GAME,
  grid: 'normal',
  muted: false,
  soundTheme: 'wood',
  volume: 0.7,
  hintsEnabled: true,
  hintsSeen: [],
  tileSize: 0,          // 0 = follow the responsive default
  showRunTags: true,
  lastRoom: '',
  narrate: false,
  typeDir: 'across',
  countdownSound: DEFAULT_COUNTDOWN,
  countdownVoice: '',
  autoDir: true,
  dragReach: 1,
  swapGuard: 0.75,
  lastRules: {},
};

/**
 * Only what the player has actually chosen is stored.
 *
 * Every preference used to be written out whenever any one of them changed —
 * defaults included — so the default of the day was frozen into every device
 * as though somebody had picked it. Changing a default then reached nobody who
 * had ever typed a name: phones that had played before the living-room
 * backdrops kept showing the old "library" wash behind the menus. Now the
 * stored record is just the choices, and a default is always the current one.
 */
/*
 * 2: only chosen values are stored. 3: the remembered match rules are trimmed
 * to their changes too, dropping the old defaults they had frozen in.
 */
const SCHEMA = 3;
let chosen: Partial<Prefs> = {};
let current: Prefs = load();

function load(): Prefs {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const stored = JSON.parse(raw) as Record<string, unknown>;
      chosen = readChosen(stored);
      if (stored['schema'] !== SCHEMA) save();       // converted once
    }
  } catch {
    chosen = {};
  }
  const out = { ...DEFAULTS, ...chosen };
  // A backdrop that has since been retired (the drawn parlour) falls back
  // to the default rather than to a blank page.
  if (!sceneFits(out.sceneMenu, 'menu')) out.sceneMenu = DEFAULT_SCENE_MENU;
  if (!sceneFits(out.sceneGame, 'board')) out.sceneGame = DEFAULT_SCENE_GAME;
  return out;
}

/**
 * The choices in a stored record.
 *
 * A record from before only the choices were stored cannot say which of its
 * values were chosen. Anything equal to today's default is treated as not
 * chosen; and the look of the menus and the board — backdrops and grid — is
 * let go entirely, because those were the values most often frozen in by
 * accident and the ones whose defaults have since been decided on purpose.
 */
function readChosen(stored: Record<string, unknown>): Partial<Prefs> {
  const { schema, ...values } = stored;
  if (schema === SCHEMA) return values as Partial<Prefs>;
  if (schema === 2) {
    const v = values as Partial<Prefs>;
    if (v.lastRules) v.lastRules = convertOldRules(v.lastRules);
    return v;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    if (k === 'sceneMenu' || k === 'sceneGame' || k === 'grid') continue;
    if (!(k in DEFAULTS)) continue;
    if (JSON.stringify(v) === JSON.stringify(DEFAULTS[k as keyof Prefs])) continue;
    out[k] = k === 'lastRules' ? convertOldRules(v as Partial<MatchConfig>) : v;
  }
  return out as Partial<Prefs>;
}

function save(): void {
  try { localStorage.setItem(KEY, JSON.stringify({ schema: SCHEMA, ...chosen })); } catch { /* ignore */ }
}

export function prefs(): Prefs { return current; }

export function setPrefs(patch: Partial<Prefs>): Prefs {
  chosen = { ...chosen, ...patch };
  current = { ...current, ...patch };
  save();
  applyDocumentPrefs();
  // For anything on screen that shows a preference, such as the board's
  // direction arrow, which the settings dialog can change mid-game.
  window.dispatchEvent(new CustomEvent(PREFS_CHANGED));
  return current;
}

/**
 * Follow the system theme while the preference is 'system'.
 *
 * Registered once. Without it, a device that flips to dark at sunset — which
 * phones do on their own — leaves the game in the theme it happened to boot
 * in for the rest of the session.
 */
let watchingSystemTheme = false;
function watchSystemTheme(): void {
  if (watchingSystemTheme) return;
  watchingSystemTheme = true;
  const mq = matchMedia('(prefers-color-scheme: light)');
  const onChange = (): void => { if (current.theme === 'system') applyDocumentPrefs(); };
  // addEventListener on MediaQueryList is recent enough that older Safari
  // still needs the deprecated form.
  if (typeof mq.addEventListener === 'function') mq.addEventListener('change', onChange);
  else (mq as MediaQueryList & { addListener(fn: () => void): void }).addListener(onChange);
}

/** Push the visual preferences onto the document root. */
export function applyDocumentPrefs(): void {
  watchSystemTheme();
  const root = document.documentElement;
  const theme = current.theme === 'system'
    ? (matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark')
    : current.theme;
  root.dataset.theme = theme;
  root.dataset.contrast = current.contrast;
  root.dataset.sceneMenu = current.sceneMenu;
  root.dataset.sceneGame = current.sceneGame;
  root.dataset.grid = current.grid;
  if (current.tileSize > 0) root.style.setProperty('--cell', `${current.tileSize}px`);
  else root.style.removeProperty('--cell');
}

export function hintDismissed(id: string): boolean {
  return !current.hintsEnabled || current.hintsSeen.includes(id);
}

export function dismissHint(id: string): void {
  if (current.hintsSeen.includes(id)) return;
  setPrefs({ hintsSeen: [...current.hintsSeen, id] });
}

export function resetHints(): void { setPrefs({ hintsSeen: [], hintsEnabled: true }); }

/**
 * This device's private identity: what proves, on reconnecting, that this is
 * the same player.
 *
 * It lives under a new key and is generated fresh. It must never be derived
 * from `nt2.device.v1`: before v0.2.7 that value *was* the public player id,
 * sent to everyone at every table this device ever sat at. Reusing it as the
 * secret meant anyone who had once shared a table with a returning player
 * could present it and become them — hashing it on the server does not help
 * when the other person already knows the input. (Independent fix review,
 * 2026-09-23.) The old key is deleted rather than kept: it is useless now, and
 * a credential nobody needs is one less thing to leak.
 *
 * Uses the engine's `uuid`, not `crypto.randomUUID` directly: that is exposed
 * only in a secure context, so over plain HTTP on a LAN address it is missing
 * and calling it throws. This runs at module load, so an exception here takes
 * the whole app down before anything renders.
 */
export function deviceSecret(): string {
  const K = 'nt2.secret.v1';
  try {
    localStorage.removeItem(LEGACY_PUBLIC_ID_KEY);
    let secret = localStorage.getItem(K);
    if (!secret) { secret = uuid(); localStorage.setItem(K, secret); }
    return secret;
  } catch {
    // Storage blocked, as in a private window. A fresh identity each load is
    // worse than a stable one but is still a working game.
    return uuid();
  }
}

/** Where v0.2.6 and earlier kept the id that was broadcast to every player. */
export const LEGACY_PUBLIC_ID_KEY = 'nt2.device.v1';
